/**
 * Rich Text Utilities for Canva-like text editing
 * Handles JSON structure for rich text with spans and formatting
 */

/**
 * Convert plain text to rich text JSON structure
 * @param {string} text - Plain text content
 * @param {Object} defaultStyle - Default text styling
 * @returns {Object} Rich text JSON structure
 */
export const createRichText = (text, defaultStyle = {}) => {
  return {
    type: 'richtext',
    content: text,
    spans: [
      {
        start: 0,
        end: text.length,
        style: {
          fontSize: defaultStyle.fontSize || 16,
          fontFamily: defaultStyle.fontFamily || 'Arial',
          fontWeight: defaultStyle.fontWeight || 'normal',
          fontStyle: defaultStyle.fontStyle || 'normal',
          color: defaultStyle.color || '#000000',
          textDecoration: defaultStyle.textDecoration || 'none',
          ...defaultStyle
        }
      }
    ]
  }
}

/**
 * Apply formatting to a text selection
 * @param {Object} richText - Rich text JSON structure
 * @param {number} start - Selection start position
 * @param {number} end - Selection end position
 * @param {Object} formatting - Formatting to apply
 * @returns {Object} Updated rich text structure
 */
export const applyFormatting = (richText, start, end, formatting) => {
  if (!richText.spans) {
    richText.spans = [{ start: 0, end: richText.content.length, style: {} }]
  }

  const newSpans = []
  
  for (const span of richText.spans) {
    // Span is completely before selection
    if (span.end <= start) {
      newSpans.push(span)
    }
    // Span is completely after selection
    else if (span.start >= end) {
      newSpans.push(span)
    }
    // Span overlaps with selection
    else {
      // Part before selection
      if (span.start < start) {
        newSpans.push({
          start: span.start,
          end: start,
          style: { ...span.style }
        })
      }
      
      // Selected part with new formatting
      newSpans.push({
        start: Math.max(span.start, start),
        end: Math.min(span.end, end),
        style: { ...span.style, ...formatting }
      })
      
      // Part after selection
      if (span.end > end) {
        newSpans.push({
          start: end,
          end: span.end,
          style: { ...span.style }
        })
      }
    }
  }
  
  return {
    ...richText,
    spans: mergeAdjacentSpans(newSpans)
  }
}

/**
 * Merge adjacent spans with identical styling
 * @param {Array} spans - Array of text spans
 * @returns {Array} Merged spans
 */
export const mergeAdjacentSpans = (spans) => {
  if (spans.length <= 1) return spans
  
  const merged = [spans[0]]
  
  for (let i = 1; i < spans.length; i++) {
    const current = spans[i]
    const previous = merged[merged.length - 1]
    
    // Check if styles are identical
    if (previous.end === current.start && 
        JSON.stringify(previous.style) === JSON.stringify(current.style)) {
      // Merge spans
      previous.end = current.end
    } else {
      merged.push(current)
    }
  }
  
  return merged
}

/**
 * Convert rich text to HTML for display
 * @param {Object} richText - Rich text JSON structure
 * @returns {string} HTML string
 */
export const richTextToHTML = (richText) => {
  if (!richText.spans || richText.spans.length === 0) {
    return richText.content || ''
  }
  
  let html = ''
  let lastEnd = 0
  
  for (const span of richText.spans) {
    // Add any unstyled text before this span
    if (span.start > lastEnd) {
      html += richText.content.slice(lastEnd, span.start)
    }
    
    // Add styled span
    const spanText = richText.content.slice(span.start, span.end)
    const style = span.style || {}
    
    let spanHtml = spanText
    
    // Apply formatting
    if (style.fontWeight === 'bold') {
      spanHtml = `<strong>${spanHtml}</strong>`
    }
    if (style.fontStyle === 'italic') {
      spanHtml = `<em>${spanHtml}</em>`
    }
    if (style.textDecoration === 'underline') {
      spanHtml = `<u>${spanHtml}</u>`
    }
    
    // Apply inline styles
    const inlineStyles = []
    if (style.color) inlineStyles.push(`color: ${style.color}`)
    if (style.fontSize) inlineStyles.push(`font-size: ${style.fontSize}px`)
    if (style.fontFamily) inlineStyles.push(`font-family: ${style.fontFamily}`)
    
    if (inlineStyles.length > 0) {
      spanHtml = `<span style="${inlineStyles.join('; ')}">${spanHtml}</span>`
    }
    
    html += spanHtml
    lastEnd = span.end
  }
  
  // Add any remaining unstyled text
  if (lastEnd < richText.content.length) {
    html += richText.content.slice(lastEnd)
  }
  
  return html
}

/**
 * Convert rich text to plain text
 * @param {Object} richText - Rich text JSON structure
 * @returns {string} Plain text content
 */
export const richTextToPlainText = (richText) => {
  return richText.content || ''
}

/**
 * Get formatting at a specific position
 * @param {Object} richText - Rich text JSON structure
 * @param {number} position - Text position
 * @returns {Object} Formatting at position
 */
export const getFormattingAtPosition = (richText, position) => {
  if (!richText.spans) return {}
  
  for (const span of richText.spans) {
    if (position >= span.start && position < span.end) {
      return span.style || {}
    }
  }
  
  return {}
}

/**
 * Insert text at a specific position
 * @param {Object} richText - Rich text JSON structure
 * @param {number} position - Insert position
 * @param {string} text - Text to insert
 * @param {Object} formatting - Formatting for inserted text
 * @returns {Object} Updated rich text structure
 */
export const insertText = (richText, position, text, formatting = {}) => {
  const newContent = richText.content.slice(0, position) + text + richText.content.slice(position)
  
  // Update spans
  const newSpans = richText.spans.map(span => {
    if (span.start >= position) {
      return {
        ...span,
        start: span.start + text.length,
        end: span.end + text.length
      }
    } else if (span.end > position) {
      return {
        ...span,
        end: span.end + text.length
      }
    }
    return span
  })
  
  // Add span for inserted text
  if (text.length > 0) {
    newSpans.push({
      start: position,
      end: position + text.length,
      style: formatting
    })
  }
  
  return {
    ...richText,
    content: newContent,
    spans: mergeAdjacentSpans(newSpans.sort((a, b) => a.start - b.start))
  }
}

/**
 * Delete text in a range
 * @param {Object} richText - Rich text JSON structure
 * @param {number} start - Start position
 * @param {number} end - End position
 * @returns {Object} Updated rich text structure
 */
export const deleteText = (richText, start, end) => {
  const deleteLength = end - start
  const newContent = richText.content.slice(0, start) + richText.content.slice(end)
  
  // Update spans
  const newSpans = []
  
  for (const span of richText.spans) {
    // Span is completely before deletion
    if (span.end <= start) {
      newSpans.push(span)
    }
    // Span is completely after deletion
    else if (span.start >= end) {
      newSpans.push({
        ...span,
        start: span.start - deleteLength,
        end: span.end - deleteLength
      })
    }
    // Span overlaps with deletion
    else {
      // Part before deletion
      if (span.start < start) {
        newSpans.push({
          ...span,
          end: start
        })
      }
      
      // Part after deletion
      if (span.end > end) {
        newSpans.push({
          ...span,
          start: start,
          end: span.end - deleteLength
        })
      }
    }
  }
  
  return {
    ...richText,
    content: newContent,
    spans: newSpans.filter(span => span.start < span.end)
  }
}
